import { TokenPair } from 'api/model';

const STORAGE_KEY = 'gtx.auth.tokens';

export type StoredTokens = {
  accessToken: string;
  refreshToken: string;
};

type SessionEndListener = () => void;

const sessionEndListeners = new Set<SessionEndListener>();

const readStoredTokens = (): StoredTokens | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StoredTokens>;
    if (!parsed.accessToken || !parsed.refreshToken) return null;

    return { accessToken: parsed.accessToken, refreshToken: parsed.refreshToken };
  } catch {
    return null;
  }
};

const writeStoredTokens = (tokens: StoredTokens | null) => {
  try {
    if (tokens) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // ignore write errors, the in-memory copy still carries the session
  }
};

let tokens: StoredTokens | null = readStoredTokens();

export const getTokens = (): StoredTokens | null => tokens;

export const getAccessToken = (): string | null => tokens?.accessToken ?? null;

export const getRefreshToken = (): string | null => tokens?.refreshToken ?? null;

export const setTokens = (pair: TokenPair) => {
  tokens = { accessToken: pair.access_token, refreshToken: pair.refresh_token };
  writeStoredTokens(tokens);
};

export const clearTokens = () => {
  tokens = null;
  writeStoredTokens(null);
};

export const onSessionEnd = (listener: SessionEndListener) => {
  sessionEndListeners.add(listener);
  return () => {
    sessionEndListeners.delete(listener);
  };
};

export const notifySessionEnd = () => {
  sessionEndListeners.forEach((listener) => listener());
};

const syncFromOtherTab = (event: StorageEvent) => {
  if (event.key !== null && event.key !== STORAGE_KEY) return;

  tokens = readStoredTokens();
  if (!tokens) notifySessionEnd();
};

if (typeof window !== 'undefined') {
  window.addEventListener('storage', syncFromOtherTab);
}
